import { useEffect, useRef, useState } from 'react';

interface TypewriterTextProps {
  texts: string[];
  speed?: number;
  delayBetween?: number;
  showCursor?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onFinish?: () => void;
  startOnView?: boolean; // if true, only starts typing when visible
}

const TypewriterText = ({
  texts,
  speed = 30,
  delayBetween = 300,
  showCursor = false,
  className = '',
  style,
  onFinish,
  startOnView = true
}: TypewriterTextProps) => {
  // deterministic initialization
  const [displayedTexts, setDisplayedTexts] = useState<string[]>(() => texts.map(() => ''));
  const [isFinished, setIsFinished] = useState(false);
  const [isVisible, setIsVisible] = useState(!startOnView);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const lineRef = useRef(0);
  const charRef = useRef(0);

  // IntersectionObserver to start when visible
  useEffect(() => {
    if (!startOnView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [startOnView]);

  // Clear any active timeout
  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Typing effect using refs to avoid stale closures
  useEffect(() => {
    if (!isVisible) return;

    clearTimer();
    setDisplayedTexts(texts.map(() => ''));
    setIsFinished(false);
    lineRef.current = 0;
    charRef.current = 0;

    const typeNext = () => {
      const lineIndex = lineRef.current;
      if (lineIndex >= texts.length) {
        setIsFinished(true);
        onFinish?.();
        return;
      }

      const currentLineText = texts[lineIndex] ?? '';

      // still typing the current line
      if (charRef.current < currentLineText.length) {
        const char = currentLineText.charAt(charRef.current);
        setDisplayedTexts((prev) => {
          const newTexts = [...prev];
          newTexts[lineIndex] = (newTexts[lineIndex] ?? '') + char;
          return newTexts;
        });
        charRef.current++;
        timerRef.current = window.setTimeout(typeNext, speed);
        return;
      }

      // finished current line
      if (charRef.current === currentLineText.length) {
        // pause before moving to next line
        timerRef.current = window.setTimeout(() => {
          lineRef.current++;
          charRef.current = 0;
          if (lineRef.current < texts.length) {
            typeNext(); // start next line
          } else {
            setIsFinished(true);
            onFinish?.();
          }
        }, delayBetween);
      }
    };

    // Start typing immediately
    typeNext();

    return () => clearTimer();
  }, [isVisible, texts, speed, delayBetween, onFinish]);

  return (
    <div ref={containerRef} className={`space-y-3 ${className}`} style={style}>
      {displayedTexts.map((line, i) => (
        <p key={i} className="inline-block">
          {line}
          {showCursor && (
            <>
              {(!isFinished && i === lineRef.current) ||
              (isFinished && i === texts.length - 1) ? (
                <span className="animate-blink">▌</span>
              ) : null}
            </>
          )}
        </p>
      ))}
    </div>
  );
};

export default TypewriterText;